import { useEffect, useMemo, useState } from "react";
import { Box, Container, Typography, CircularProgress, Button } from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import GalleryImageViewer from "../Admin/Gallery/GalleryImageViewer";

function HomeGallerySection() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const [galleries, setGalleries] = useState([]);
  const [selectedGalleryId, setSelectedGalleryId] = useState("");
  const [images, setImages] = useState([]);
  const [loadingGalleries, setLoadingGalleries] = useState(true);
  const [loadingImages, setLoadingImages] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const visibleLimit = isMobile ? 4 : 6;

  useEffect(() => {
    loadGalleries();
  }, []);

  useEffect(() => {
    if (!selectedGalleryId) return;
    loadImages(selectedGalleryId);
  }, [selectedGalleryId]);

  const selectedGallery = useMemo(() => {
    return galleries.find((gallery) => String(gallery.id) === String(selectedGalleryId)) || null;
  }, [galleries, selectedGalleryId]);

  const visibleImages = useMemo(() => {
    return images.slice(0, visibleLimit);
  }, [images, visibleLimit]);

  const loadGalleries = async () => {
    setLoadingGalleries(true);

    try {
      const res = await fetch("/gallery/public/list/");
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];

      setGalleries(list);

      if (list.length) {
        setSelectedGalleryId(String(list[0].id));
      }
    } catch (error) {
      console.error(error);
      setGalleries([]);
      setSelectedGalleryId("");
    } finally {
      setLoadingGalleries(false);
    }
  };

  const loadImages = async (galleryId) => {
    setLoadingImages(true);

    try {
      const res = await fetch(`/gallery/public/images/${galleryId}/`);
      const data = await res.json();
      setImages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  const openViewer = (index) => {
    setCurrentIndex(index);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
  };

  if (loadingGalleries) {
    return (
      <Box sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        </Container>
      </Box>
    );
  }

  if (!galleries.length) {
    return null;
  }

  const mobileColumns =
    visibleImages.length === 1 ? "1fr" : visibleImages.length === 2 ? "1fr 1fr" : "1fr 1fr";

  return (
    <>
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: "center",
              mb: { xs: 1.2, md: 1.5 },
              fontWeight: 800,
              fontSize: { xs: "1.8rem", md: "2.5rem" },
            }}
          >
            {selectedGallery?.name || "Gallery"}
          </Typography>

          <Typography
            sx={{
              textAlign: "center",
              mb: { xs: 2.8, md: 3.2 },
              color: "text.secondary",
              fontWeight: 500,
              fontSize: { xs: "0.96rem", md: "1rem" },
            }}
          >
            Relive the best moments of the tournament
          </Typography>

          <Box
            sx={{
              borderRadius: "24px",
              border: "1px solid rgba(140,15,44,0.12)",
              backgroundColor: "#fff",
              p: { xs: 1.2, sm: 1.5 },
              mb: { xs: 3, md: 4 },
              boxShadow: "0 10px 30px rgba(15,23,42,0.05)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.8,
                mb: 1.2,
                px: 0.4,
              }}
            >
              <PhotoLibraryRoundedIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography
                sx={{
                  fontWeight: 800,
                  color: "text.primary",
                  fontSize: ".95rem",
                }}
              >
                Select a gallery
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                overflowX: "auto",
                pb: 0.2,
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(140,15,44,0.18)",
                  borderRadius: "999px",
                },
              }}
            >
              {galleries.map((gallery) => {
                const active = String(selectedGalleryId) === String(gallery.id);

                return (
                  <Button
                    key={gallery.id}
                    onClick={() => setSelectedGalleryId(String(gallery.id))}
                    variant={active ? "contained" : "outlined"}
                    sx={{
                      minWidth: { xs: 150, sm: 180 },
                      height: 46,
                      borderRadius: "14px",
                      px: 2.2,
                      flexShrink: 0,
                      fontWeight: 800,
                      borderColor: "rgba(140,15,44,0.18)",
                      color: active ? "#fff" : "primary.main",
                      backgroundColor: active ? "primary.main" : "#fff",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {gallery.name}
                  </Button>
                );
              })}
            </Box>
          </Box>

          {loadingImages ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : !visibleImages.length ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontWeight: 600,
                  textAlign: "center",
                }}
              >
                No images available in this gallery.
              </Typography>
            </Box>
          ) : isMobile ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: mobileColumns,
                gap: 0.7,
              }}
            >
              {visibleImages.map((img, index) => (
                <Box
                  key={img.id}
                  onClick={() => openViewer(index)}
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: "14px",
                    cursor: "zoom-in",
                    aspectRatio: "1 / 1",
                    boxShadow: "0 10px 30px rgba(15,23,42,0.12)",
                  }}
                >
                  <Box
                    component="img"
                    src={img.image}
                    alt=""
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />

                  {index === 0 && (
                    <>
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background: "linear-gradient(180deg, rgba(0,0,0,0.00) 45%, rgba(0,0,0,0.38) 100%)",
                          pointerEvents: "none",
                        }}
                      />

                      <Box
                        sx={{
                          position: "absolute",
                          left: 10,
                          bottom: 10,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.7,
                          px: 1.2,
                          py: 0.7,
                          borderRadius: "999px",
                          backgroundColor: "rgba(255,255,255,0.16)",
                          color: "#fff",
                          backdropFilter: "blur(10px)",
                          boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                          pointerEvents: "none",
                        }}
                      >
                        <TouchAppRoundedIcon sx={{ fontSize: 20 }} />
                        <Typography
                          sx={{
                            fontSize: "0.78rem",
                            fontWeight: 700,
                            lineHeight: 1,
                            letterSpacing: "0.01em",
                            color: "#fff",
                          }}
                        >
                          Tap to enlarge
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box className="row g-3 justify-content-center">
              {visibleImages.map((img, index) => (
                <Box key={img.id} className="col-6 col-md-4 col-lg-3">
                  <Box
                    onClick={() => openViewer(index)}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: "16px",
                      cursor: "zoom-in",
                      aspectRatio: "1 / 1",
                      boxShadow: "0 12px 34px rgba(15,23,42,0.10)",
                      transition: "transform 0.25s ease, box-shadow 0.25s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 18px 42px rgba(15,23,42,0.16)",
                      },
                      "&:hover .home-gallery-image": {
                        transform: "scale(1.06)",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={img.image}
                      alt=""
                      className="home-gallery-image"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        transition: "transform 0.35s ease",
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 3, md: 4 } }}>
            <Button
              variant="contained"
              onClick={() => navigate("/galeria")}
              sx={{
                px: 3.5,
                borderRadius: "12px",
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              View full gallery
            </Button>
          </Box>
        </Container>
      </Box>

      <GalleryImageViewer
        open={viewerOpen}
        images={visibleImages}
        currentIndex={currentIndex}
        onClose={closeViewer}
        onChangeIndex={setCurrentIndex}
        galleryName={selectedGallery?.name || "Galería"}
      />
    </>
  );
}

export default HomeGallerySection;